import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Activity, AlertTriangle, BarChart3, Download, HeartPulse, RefreshCcw, ShieldCheck } from "lucide-react";

const STATUS_COLOR = { ok: "text-emerald-300", warn: "text-amber-300", fail: "text-rose-300" };

export default function HealthPage() {
  const qc = useQueryClient();
  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => api.get("/automation/health").then((r) => r.data),
    refetchInterval: 60000,
  });
  const analytics = useQuery({
    queryKey: ["analytics"],
    queryFn: () => api.get("/automation/analytics").then((r) => r.data),
    refetchInterval: 60000,
  });
  const alerts = useQuery({
    queryKey: ["alerts"],
    queryFn: () => api.get("/automation/alerts?limit=50").then((r) => r.data),
    refetchInterval: 30000,
  });

  const runAudit = useMutation({
    mutationFn: () => api.post("/automation/health/run").then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["health"] }); toast.success("AUDIT_DONE"); },
  });
  const ack = useMutation({
    mutationFn: (id) => api.post(`/automation/alerts/${id}/ack`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const backup = async () => {
    try {
      const { data } = await api.get("/automation/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `mindstein-backup-${new Date().toISOString().slice(0, 19)}.json`;
      a.click();
      toast.success("BACKUP_DOWNLOADED");
    } catch (e) {
      toast.error("BACKUP_FAILED");
    }
  };

  const h = health.data || {};
  const a = analytics.data || {};
  const status = h.status || "unknown";

  return (
    <div data-testid="health-page" className="p-4 space-y-4">
      <header className="flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-mono inline-flex items-center gap-2">
            <HeartPulse size={12} /> SYSTEM_HEALTH
          </div>
          <h1 className="font-mono text-2xl tracking-tight mt-1">Daily Audit & Analytics</h1>
          <p className="text-[12px] text-zinc-500 max-w-2xl mt-1">
            Scheduler auto-runs the audit each cycle (~30 min). Failing checks emit alerts you can acknowledge below.
          </p>
        </div>
        <div className="ml-auto flex items-end gap-3">
          <div className="border border-[#27272A] p-2 min-w-[120px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">OVERALL</div>
            <div className={"text-2xl font-mono uppercase mt-1 " + (STATUS_COLOR[status] || "text-zinc-300")}>{status}</div>
          </div>
          <button
            data-testid="run-audit"
            onClick={() => runAudit.mutate()}
            disabled={runAudit.isPending}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-emerald-400 text-emerald-200 hover:bg-emerald-400 hover:text-black disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCcw size={11} /> {runAudit.isPending ? "RUNNING…" : "RUN_AUDIT"}
          </button>
          <button
            data-testid="trigger-backup"
            onClick={backup}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] inline-flex items-center gap-2"
          >
            <Download size={11} /> BACKUP_JSON
          </button>
        </div>
      </header>

      {/* CHECKS */}
      <section className="border border-[#27272A]">
        <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
          <ShieldCheck size={11} /> CHECKS · {h.duration_ms ?? 0}ms · {h.finished_at?.slice(0, 19).replace("T", " ") || "—"}
        </div>
        <table className="w-full text-[11px] font-mono">
          <thead className="text-zinc-500">
            <tr className="border-b border-[#161616]">
              <th className="text-left p-2 uppercase">CHECK</th>
              <th className="text-left p-2 uppercase">STATUS</th>
              <th className="text-left p-2 uppercase">DETAIL</th>
            </tr>
          </thead>
          <tbody>
            {(h.checks || []).map((c, i) => (
              <tr key={i} data-testid={`check-row-${c.check}`} className="border-b border-[#161616]">
                <td className="p-2 text-white">{c.check}</td>
                <td className={"p-2 uppercase " + (STATUS_COLOR[c.status] || "text-zinc-300")}>{c.status}</td>
                <td className="p-2 text-zinc-400">{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ANALYTICS */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="border border-[#27272A]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300 inline-flex items-center gap-2">
            <BarChart3 size={11} /> MOST_USED_TOPICS
          </div>
          <table className="w-full text-[11px] font-mono">
            <thead className="text-zinc-500">
              <tr><th className="text-left p-2 uppercase">TOPIC</th><th className="text-right p-2 uppercase">ACTIVE</th><th className="text-right p-2 uppercase">AVG_RANK</th></tr>
            </thead>
            <tbody>
              {(a.most_used_topics || []).map((t) => (
                <tr key={t.topic_slug} className="border-b border-[#161616]">
                  <td className="p-2 uppercase">{t.topic_slug}</td>
                  <td className="p-2 text-right tabnum">{t.active_ideas}</td>
                  <td className="p-2 text-right tabnum text-emerald-300">{Number(t.avg_rank || 0).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-[#27272A]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300 inline-flex items-center gap-2">
            <BarChart3 size={11} /> STRONGEST_SOURCES
          </div>
          <table className="w-full text-[11px] font-mono">
            <thead className="text-zinc-500">
              <tr><th className="text-left p-2 uppercase">DOMAIN</th><th className="text-right p-2 uppercase">CRED</th><th className="text-right p-2 uppercase">REUSE</th><th className="text-right p-2 uppercase">STR</th></tr>
            </thead>
            <tbody>
              {(a.strongest_sources || []).map((s, i) => (
                <tr key={i} className="border-b border-[#161616]">
                  <td className="p-2 text-white truncate max-w-[220px]">{s.domain}</td>
                  <td className="p-2 text-right tabnum">{s.credibility_score}</td>
                  <td className="p-2 text-right tabnum">{s.repeat_count}</td>
                  <td className="p-2 text-right tabnum text-emerald-300">{Number(s.strength).toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-[#27272A]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-rose-300 inline-flex items-center gap-2">
            <BarChart3 size={11} /> MOST_USED_BOT_MODES_7D
          </div>
          <table className="w-full text-[11px] font-mono">
            <thead className="text-zinc-500"><tr><th className="text-left p-2 uppercase">MODE</th><th className="text-right p-2 uppercase">USES</th></tr></thead>
            <tbody>
              {(a.most_copied_prompts || []).map((p) => (
                <tr key={p.mode} className="border-b border-[#161616]">
                  <td className="p-2 uppercase">{p.mode}</td><td className="p-2 text-right tabnum">{p.uses}</td>
                </tr>
              ))}
              {(a.most_copied_prompts || []).length === 0 && (
                <tr><td colSpan={2} className="p-4 text-center text-zinc-500">NO_BOT_USAGE_YET</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border border-[#27272A]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300 inline-flex items-center gap-2">
            <BarChart3 size={11} /> MOST_MIXED_MODES
          </div>
          <table className="w-full text-[11px] font-mono">
            <thead className="text-zinc-500"><tr><th className="text-left p-2 uppercase">MODE</th><th className="text-right p-2 uppercase">MIXES</th></tr></thead>
            <tbody>
              {(a.most_mixed_modes || []).map((p) => (
                <tr key={p.mode} className="border-b border-[#161616]">
                  <td className="p-2 uppercase">{p.mode}</td><td className="p-2 text-right tabnum">{p.count}</td>
                </tr>
              ))}
              {(a.most_mixed_modes || []).length === 0 && (
                <tr><td colSpan={2} className="p-4 text-center text-zinc-500">NO_MIXING_YET</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ALERTS */}
      <section className="border border-[#27272A]">
        <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-rose-300 inline-flex items-center gap-2">
          <AlertTriangle size={11} /> ALERTS · {alerts.data?.length ?? 0}
        </div>
        <table className="w-full text-[11px] font-mono">
          <thead className="text-zinc-500">
            <tr className="border-b border-[#161616]">
              <th className="text-left p-2 uppercase">WHEN</th>
              <th className="text-left p-2 uppercase">KIND</th>
              <th className="text-left p-2 uppercase">SEV</th>
              <th className="text-left p-2 uppercase">MESSAGE</th>
              <th className="text-right p-2 uppercase">ACK</th>
            </tr>
          </thead>
          <tbody>
            {(alerts.data || []).map((al) => (
              <tr key={al.id} data-testid={`alert-row-${al.id}`} className="border-b border-[#161616]">
                <td className="p-2 text-zinc-500">{al.created_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="p-2 uppercase">{al.kind}</td>
                <td className={"p-2 uppercase " + (STATUS_COLOR[al.severity === "error" ? "fail" : al.severity] || "text-zinc-300")}>{al.severity}</td>
                <td className="p-2 text-zinc-300 max-w-[480px] truncate" title={al.message}>{al.message}</td>
                <td className="p-2 text-right">
                  {al.acknowledged ? <span className="text-zinc-600">✓</span> : (
                    <button onClick={() => ack.mutate(al.id)} className="text-zinc-400 hover:text-emerald-300">ACK</button>
                  )}
                </td>
              </tr>
            ))}
            {(alerts.data || []).length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-zinc-500">NO_ALERTS · everything is green</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <div className="text-[10px] font-mono text-zinc-500">
        Backups + analytics + audits are stored in <code className="text-zinc-300">fde_backups</code>,{" "}
        <code className="text-zinc-300">fde_analytics_snapshots</code>,{" "}
        <code className="text-zinc-300">fde_health_audits</code>.
      </div>
    </div>
  );
}
